import { Component } from '@angular/core';
import { Client, CommandStatus, PageInfo, PermissionsEnum, SchedulerStatusHistoryDataIn, SchedulerStatusHistoryDataOut, SortingType } from '../../../api/api-reference';
import { Router, RouterLink } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../../../services/auth.service';
import { ClipboardService } from '../../../services/clipboard.service';
import { FileService } from '../../../services/file.service';
import { processDateRange } from '../../../helper/date-utils';
import { generateFileNameAppendix } from '../../../helper/download-filename';
import { DateRangeInput } from '../../core/models/date-range-input';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { DatepickerComponent } from '../../core/components/datepicker/datepicker.component';
import { SortComponent } from '../../core/components/sort/sort.component';
import { SearchComponent } from '../../core/components/search/search.component';
import { PaginationComponent } from '../../core/components/pagination/pagination.component';
import { IsNewDirective } from '../../../directives/is-new.directive';
import { StatusComponent } from '../../core/components/status/status.component';

@Component({
  selector: 'app-job-status-history',
  standalone: true,
  imports: [TranslateModule,
    CommonModule,
    AngularSvgIconModule,
    DatepickerComponent,
    SortComponent,
    SearchComponent, 
    RouterLink, 
    PaginationComponent,
    IsNewDirective,
    StatusComponent
  ],
  templateUrl: './job-status-history.component.html',
  styleUrl: './job-status-history.component.scss'
})
export class JobStatusHistoryComponent{
  public pageSize: number = 10;
  public pageNumber: number = 1;
  public currentPage: number = 1;
  public numberOfCommands: number = 0;
  public lastPageSize: number = 1;
  commandStatuses = [CommandStatus.Pending, CommandStatus.Success, CommandStatus.Failed].map(s => s.toUpperCase());

  data: SchedulerStatusHistoryDataOut[] | undefined = [];
  isDatepickerOpen = false;
  readonly dayInMs = 24 * 60 * 60 * 1000;
  PermissionEnum = PermissionsEnum;

  requestData = new SchedulerStatusHistoryDataIn({
    pageInfo: new PageInfo({ pageSize: this.pageSize, page: this.pageNumber }),
    search: undefined,
    startTime: undefined,
    endTime: undefined,
    sorting: SortingType.Descending,
    status: undefined,
  });

  constructor(
    private client: Client,
    private toastr: ToastrService,
    private router: Router,
    public authService: AuthService,
    public clipboardService: ClipboardService,
    private fileService: FileService
  ) {
    this.reloadTable(1);
  }

  printResponseOfCommand(event: MouseEvent, cmd: SchedulerStatusHistoryDataOut) {
    if (cmd.status === CommandStatus.Failed) {
      event.stopPropagation();
      this.toastr.error('Command failed');
    }
  }

  reloadTable(page?: number) {
    this.pageNumber = page || 0;
    if (page) this.requestData!.pageInfo!.page = this.pageNumber;
    this.client.jobStatusHistoryQuery(this.requestData).subscribe({
      next: (response) => {
        this.data = response.data;
        this.numberOfCommands = response.count || 0;
        this.lastPageSize = Math.ceil(this.numberOfCommands / this.pageSize);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  pageChange(value: number) {
    this.currentPage = value;
    this.reloadTable(this.currentPage);
  }

  searchChange(searchText: string) {
    this.requestData.search = searchText;
    this.reloadTable(1);
  }

  toggleModal() {
    this.isDatepickerOpen = !this.isDatepickerOpen;
  }

  onSortOption(selectedSort?: SortingType) {
    if (selectedSort) this.requestData.sorting = selectedSort;
    this.currentPage = 1;
    this.reloadTable(1);
  }
  onStatusOption(selectedStatus?: string) {
    if (selectedStatus !== undefined) {
      this.requestData.status = selectedStatus === 'ALL'
        ? undefined
        : Object.values(CommandStatus).find(v => v.toUpperCase() === selectedStatus);
    }
    this.currentPage = 1;
    this.reloadTable(1);
  }

  selectedDate: string | null = '';
  onDateRangeChanged(input: DateRangeInput): void {
    const result = processDateRange(input);
    this.requestData.startTime = result.startDate;
    this.requestData.endTime = result.endDate;
    this.selectedDate = result.selectedDate;
    this.currentPage = 1;
    this.filterDatesInTable();
  }

  clearDates(): void {
    this.requestData.startTime = undefined;
    this.requestData.endTime = undefined;
    this.reloadTable();
  }

  download() {
    const fileNameApendix = generateFileNameAppendix();

    this.client.downloadJobStatusHistoryQuery(this.requestData).subscribe({
      next: (response) => {
        this.fileService.downloadFile(
          response.data,
          response.fileName || `JobStatusHistory${fileNameApendix}`,
        );
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  filterDatesInTable() {
    this.client.jobStatusHistoryQuery(this.requestData).subscribe({
      next: (response) => {
        this.data = response.data;
        this.numberOfCommands = response.count || 0;
        this.lastPageSize = Math.ceil(this.numberOfCommands / this.pageSize);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }
}
